import { useIsHydrated } from 'client/widget-components/customHooks/useIsHydrated';
import React, { useCallback, useContext, useState } from 'react';
import { useBookingContext } from '../../../Context';
import useCalEventData from '../../../hooks/useCalEventData';
import { useCommonBookerProps } from '../../../hooks/useCommonBookerProps';
import { BookerStateContext } from '../../../hooks/useRuntimeCalBookingState';
import { BookerStoreValues, CalBookerStep } from '../../../types';
import { validateBookerReceivedParams } from '../../../utils';
import type { BookerStepViewProps } from './bookerStepView';
import LegacyBookerStep from './LegacyBookerStep';
import NativeBookerStep from './NativeBookerStep';

const BookerStep = () => {
    const hydrated = useIsHydrated();
    const bookerContext = useBookingContext();
    const [bookingState, bookingActions] = useContext(BookerStateContext);

    const [calBookerStep, setCalBookerStep] = useState<CalBookerStep | null>(
        null
    );

    const { organizationId, clientId, teamId, isNativeBookerEnabled } =
        bookerContext;

    const { isLoading, language, siteLocale, apiUrl, bookerKey, eventSlug } =
        bookingState;

    const { setCalEventData, setIsLoading } = bookingActions;

    useCalEventData(
        {
            teamId: teamId || undefined,
            eventSlug: eventSlug || '',
            organizationId,
        },
        {
            onSuccess: setCalEventData,
            onError: () => setIsLoading(false),
        }
    );

    const isBookingActive = !!eventSlug && !!clientId && !!organizationId;

    // validate all required props are present for booker atom when booking is active
    validateBookerReceivedParams(bookerContext, isBookingActive);

    const onBookerStateChange = useCallback(
        (bookerState: BookerStoreValues) => {
            setCalBookerStep(bookerState.state as CalBookerStep);
            if (
                bookerState.state === CalBookerStep.SELECTING_TIME &&
                isLoading
            ) {
                setIsLoading(false);
            }
        },
        [isLoading, setIsLoading]
    );

    // We want to render the booker only after the component is hydrated on
    // client side since it's a lazy loaded component and we want to keep the
    // server and client rendering consistent.
    const shouldRenderBooker = hydrated && isBookingActive;
    const shouldRenderLoadingSkeleton = isBookingActive && isLoading;
    const shouldRenderBookingMarkup = !isBookingActive;

    const calBookerWrapperClassNames: string[] = [bookerContext.designStyle];
    if (isBookingActive) {
        calBookerWrapperClassNames.push(
            isLoading ? 'booker-loading' : 'booker-ready'
        );
    }

    const commonBookerProps = useCommonBookerProps({ calBookerStep });

    const viewProps: BookerStepViewProps = {
        shouldRenderBooker,
        shouldRenderLoadingSkeleton,
        shouldRenderBookingMarkup,
        onBookerStateChange,
        bookerKey,
        clientId,
        apiUrl,
        language,
        siteLocale,
        isBookingDryRun: bookerContext.isBookingDryRun,
        commonBookerProps,
    };

    return (
        <div
            className={`booker-wrapper ${calBookerWrapperClassNames.join(' ')}`}
        >
            {isNativeBookerEnabled ? (
                <NativeBookerStep {...viewProps} />
            ) : (
                <LegacyBookerStep {...viewProps} />
            )}
        </div>
    );
};

export default BookerStep;
